import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Avatar,
  Badge,
  Alert,
  Button,
  Card,
  Filters,
  NotProvided,
  Page,
  PageHeader,
  Pagination,
  Table,
  type Action,
  type FilterFieldSlot,
} from '@evogard/ui';
import { count, formatPhone } from '@evogard/domain';
import { useSessionContext } from '../../../lib/session.js';
import { useDebouncedTerm } from '../../../lib/debounce.js';
import { DataCell } from '../DataCell.js';
import { ThirdPartyForm } from './ThirdPartyForm.js';
import { VehicleForm } from './VehicleForm.js';
import {
  EMPTY_THIRD_PARTY_VEHICLE,
  ThirdPartyVehicleField,
  type ThirdPartyVehicleValue,
} from './ThirdPartyVehicleField.js';
import {
  MIN_SEARCH,
  useThirdPartyPermissions,
  useThirdPartySearch,
} from './useThirdParties.js';

type WithVehicle = '' | 'sim' | 'nao';

/**
 * Sinistros › Cadastros › Terceiros — as pessoas de fora da associação
 * envolvidas num sinistro, e os veículos delas.
 *
 * ## A tabela é a de Comercial › Vendas, de propósito
 *
 * O mesmo esquema, pedido pelo dono: a **busca** na barra vale ao digitar; o
 * **painel de filtros** ao lado dela só vale quando alguém confirma (ver o
 * cabeçalho de `Filters`); as **ações** de cada linha ficam nas reticências;
 * o **esqueleto** tem o tamanho da página; e a **paginação** é do servidor,
 * com o total no título do cartão.
 *
 * ## Cadastrar começa por buscar
 *
 * "Novo terceiro" abre o mesmo `ThirdPartyVehicleField` que o aviso vai usar:
 * primeiro a busca, e só depois o cadastro. O terceiro não tem documento, e
 * sem procurar antes a mesma pessoa vira duas linhas que não se juntam depois.
 * Quando um veículo fica escolhido — de uma pessoa que já existia ou de uma
 * acabada de cadastrar —, a tela abre a ficha dela.
 */
export function ThirdPartiesScreen() {
  const { data: context } = useSessionContext();
  const { canCreate, canEdit } = useThirdPartyPermissions();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const term = useDebouncedTerm(search);
  const [withVehicle, setWithVehicle] = useState<WithVehicle>('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<ThirdPartyVehicleValue>(EMPTY_THIRD_PARTY_VEHICLE);

  /* busca com uma letra casaria quase todo mundo; abaixo do mínimo, é a lista inteira */
  const effective = term.trim().length >= MIN_SEARCH ? term : '';
  const query = useThirdPartySearch(effective, {
    page,
    perPage,
    withVehicle: withVehicle || undefined,
  });
  const result = query.data;
  const hasFilter = effective !== '' || withVehicle !== '';

  const openFile = (id: number) =>
    void navigate({ to: '/claims/third-parties/$id', params: { id: String(id) } });

  const fields: FilterFieldSlot[] = [
    {
      type: 'selecao',
      name: 'withVehicle',
      label: 'Veículo',
      placeholder: 'Todos',
      options: [
        { value: 'sim', label: 'Com veículo — já pode entrar num aviso' },
        { value: 'nao', label: 'Sem veículo — falta vincular o carro' },
      ],
    },
  ];

  return (
    <Page measure="cheia">
      <PageHeader
        title="Terceiros"
        actions={
          /* Esconder é cortesia; a rota continua exigindo `claims.third_parties:criar`. */
          canCreate &&
          !creating && (
            <Button icon="mais" onClick={() => setCreating(true)}>
              Novo terceiro
            </Button>
          )
        }
      >
        As pessoas de fora da associação envolvidas num sinistro de{' '}
        {context?.currentTenant.nome}, e os veículos delas. É daqui que o aviso do
        terceiro escolhe o carro.
      </PageHeader>

      {creating && canCreate && (
        <Card
          title="Novo terceiro"
          actions={
            <Button
              variant="discreto"
              size="sm"
              onClick={() => {
                setCreating(false);
                setDraft(EMPTY_THIRD_PARTY_VEHICLE);
              }}
            >
              Fechar
            </Button>
          }
        >
          <ThirdPartyVehicleField
            label="Terceiro e veículo"
            value={draft}
            onChange={(next) => {
              setDraft(next);
              if (next.thirdPartyId !== null && next.thirdPartyVehicleId !== null) {
                openFile(next.thirdPartyId);
              }
            }}
          />
        </Card>
      )}

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {/* Erro sem dado: o aviso acima é a resposta inteira — a tabela diria
          "nenhum terceiro" logo abaixo, e é a afirmação que parece fato. */}
      {(!query.isError || result) && (
        <Card
          title={
            result === undefined
              ? 'Carregando…'
              : count(result.total, 'terceiro', 'terceiros')
          }
          descricao={
            withVehicle === 'sim'
              ? 'Só quem já tem veículo ativo.'
              : withVehicle === 'nao'
                ? 'Só quem ainda não tem veículo — não pode entrar num aviso.'
                : 'Todos os terceiros desta base.'
          }
          bleed
        >
          <Table
            paginate={false}
            search={{
              label: 'Pesquisar terceiros',
              placeholder: 'Nome, telefone ou placa',
              value: search,
              onChange: (value) => {
                setSearch(value);
                setPage(1);
              },
            }}
            toolbar={
              <Filters
                values={{ withVehicle }}
                fields={fields}
                onApply={(values) => {
                  setWithVehicle(String(values.withVehicle ?? '') as WithVehicle);
                  setPage(1);
                }}
              />
            }
            label="Terceiros cadastrados nesta base"
            items={result?.items ?? []}
            loading={query.isLoading}
            /* o esqueleto tem o tamanho da página, e não as três linhas de fábrica —
               senão o cartão cresce sob o ponteiro quando o conteúdo chega */
            skeletonRows={perPage}
            rowKey={(t) => String(t.id)}
            rowTitle={(t) => t.name}
            rowSubtitle={(t) => formatPhone(t.phone)}
            rowIcon="terceiro"
            /*
             * As ações nas reticências, como em Vendas. Corrigir e vincular abrem
             * no painel os MESMOS formulários da ficha — e eles funcionam ali
             * porque não desenham `<form>` (`<Form nested>`).
             */
            rowActions={(t): Action[] => [
              { label: 'Abrir ficha', icon: 'seta-direita', run: () => openFile(t.id) },
              ...(canEdit
                ? [
                    {
                      label: 'Corrigir',
                      icon: 'etiqueta' as const,
                      form: (close: () => void) => (
                        <ThirdPartyForm editing={t} onSaved={close} onCancel={close} />
                      ),
                    },
                  ]
                : []),
              ...(canCreate
                ? [
                    {
                      label: 'Vincular veículo',
                      icon: 'veiculo' as const,
                      form: (close: () => void) => (
                        <VehicleForm
                          thirdPartyId={t.id}
                          onSaved={close}
                          onCancel={close}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
            empty={
              hasFilter
                ? 'Nenhum terceiro com esses filtros. Isso é sobre o recorte, não sobre a base.'
                : 'Nenhum terceiro cadastrado nesta base ainda. O terceiro entra aqui quando um ' +
                  'sinistro envolve alguém de fora da associação — pelo botão "Novo terceiro" ou ' +
                  'pelo próprio aviso.'
            }
            columns={[
              {
                title: 'Nome',
                alignment: 'inicio' as const,
                minWidth: '15rem',
                cell: (t) => (
                  /* A pessoa se reconhece pelas iniciais, e não por um ícone
                     de pessoa igual em todas as linhas. */
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Avatar nome={t.name} size="md" />
                    <p className="truncate font-semibold">{t.name}</p>
                  </div>
                ),
              },
              {
                title: 'Telefone',
                minWidth: '11rem',
                cell: (t) => (
                  <DataCell icon="telefone">
                    <span className="tabular-nums">{formatPhone(t.phone)}</span>
                  </DataCell>
                ),
              },
              {
                title: 'E-mail',
                minWidth: '13rem',
                cell: (t) => (
                  <DataCell icon="email" hint={t.email ?? undefined}>
                    {t.email ? (
                      <span className="truncate">{t.email}</span>
                    ) : (
                      <NotProvided />
                    )}
                  </DataCell>
                ),
              },
              {
                title: 'Veículos',
                minWidth: '14rem',
                cell: (t) =>
                  t.vehicles.length === 0 ? (
                    /* Sem veículo o sinistro do terceiro não abre — é estado, e
                       leva o selo de estado, e não um texto amarelo solto. */
                    <Badge tone="alerta">Nenhum veículo</Badge>
                  ) : (
                    <DataCell icon="veiculo">
                      {t.vehicles.map((v) => (
                        /* O modelo na dica: a linha é uma só, e a placa é o que
                           se procura numa lista de terceiros. */
                        <span
                          key={v.id}
                          title={`${v.model} · ${v.manufactureYear}/${v.modelYear}`}
                          className="rounded-pequeno border border-borda bg-superficie-suave px-1.5 shrink-0 font-mono text-xs tabular-nums"
                        >
                          {v.plate}
                        </span>
                      ))}
                    </DataCell>
                  ),
              },
            ]}
          />
          {result && (
            <Pagination
              page={result.page}
              perPage={result.perPage}
              total={result.total}
              onChange={setPage}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
              label="terceiros"
              /* o cartão é `bleed`; o espaçamento é de quem entra nele */
              className="border-t border-borda-suave px-5 py-4"
            />
          )}
        </Card>
      )}
    </Page>
  );
}
